import { reactive, computed, ref } from "vue";

import { apiGet, Api } from "./Backend";
import { Configurator, productFromMastercodes } from "./configurator/Configurator";
import { persistSelection } from "./Share";

import type { TFloorplan, TMaterialAttribute } from "./configurator/Types";

function buildProductOptions(master: any[]) {
    return master.map((m: any) => {
        const codes = m.object_master_code.map((x: any) => x.master_code.master_code);
        return { value: JSON.stringify(codes), title: `${ m.name_fr } ${ codes.join(" ") }`, codes };
    });
}

function aggregateMaterialAttributes(rows: { attribute: string; value: string }[]): TMaterialAttribute[] {
    const map = new Map<string, Set<string>>();
    for (const r of rows) {
        const code = r.attribute.toUpperCase();
        const val = r.value.toUpperCase();
        if (!map.has(code)) { map.set(code, new Set()); }
        map.get(code)!.add(val);
    }
    return [...map.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([code, set]) => { return { code, values: [...set].sort((a, b) => a.localeCompare(b)) }; });
}

export function useConfigurator() {
    const ready = ref(false);
    const productOptions = ref<{ value: string; title: string; codes: string[] }[]>([]);

    const configurator = reactive(new Configurator({
        floorplans: [],
        materialAttributes: [],
        cameras: [],
        currentFloorplanId: "0",
        // default single chair as placeholder
        primary: productFromMastercodes(0, ["30223/1bzpzczfzb"], false),
        secondary: null,
    })) as Configurator;

    const hasChairs = computed(() => configurator.hasChairs);
    const floorplanList = computed(() => configurator.floorplans.map((f) => {
        return {
            value: f.nb_floorplan, label: f.filename.split(".")[0], chairs: f.nb_pivot_points - 1,
        };
    }));
    const sceneJson = computed(() => configurator.toSceneJson());

    async function init() {
        console.log("Initializing configurator...");
        const [floorplans, master, attributes, cameras] = await Promise.all([
            apiGet<TFloorplan[]>(Api.floorplans()),
            apiGet<any[]>(Api.mastercode()),
            apiGet<{ attribute: string; value: string }[]>(Api.materialattributes()),
            apiGet<any[]>(Api.cameras()),
        ]);
        configurator.floorplans = floorplans;
        configurator.materialAttributes = aggregateMaterialAttributes(attributes);
        configurator.cameras = cameras;
        productOptions.value = buildProductOptions(master);

        // persistSelection(configurator);
        ready.value = true;
    }

    function commitSelection() {
        persistSelection(configurator);
        return JSON.stringify(sceneJson.value);
    }

    return { cfg: configurator, productOptions, hasChairs, floorplanList, sceneJson, commitSelection, init, ready };
}
